import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { updateLearner, getBuddies } from '../../api/learners';
import { useLearnerStore } from '../../stores/learnerStore';
import { useLearner } from '../../hooks/useLearner';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import { useOnboardingGuard } from '../../hooks/useOnboardingGuard';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import ToolTipLing from '../../components/common/ToolTipLing';
import OnboardingPageCard from '../../components/common/OnboardingPageCard';
import s from './BuddyPage.module.css';
import shared from './OnboardingPages.module.css';

// Per-buddy trait copy. The buddy roster (id/slug/name/sort_order) is fetched
// from GET /buddies; trait copy is UI-side because it's not data — it's
// translation-bound flavour text that lives next to the page.
const TRAITS = {
  ling: 'Warm-hearted and brave, she keeps the whole crew together.',
  ming: 'The wise one who always has the right word at the right moment.',
  feng: 'Speedy, mischievous, and full of surprises around every corner.',
  yong: 'The scientist of the group who loves solving problems with logic.',
  meng: 'A gentle dreamer who finds magic hiding in everyday moments.',
  qi:   'Curious and fearless, always first to race toward the unknown.',
};

const STEP_COUNT = 5;

function mod(n, m) {
  return ((n % m) + m) % m;
}

export default function BuddyPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();

  useOnboardingGuard(2);

  const queryClient = useQueryClient();
  const setActiveLearnerId = useLearnerStore((s) => s.setActiveLearnerId);
  const { data: activeLearner } = useLearner();

  // Roster comes from the API (ADR-048 — buddies as a first-class entity).
  // staleTime: Infinity because the seed never changes at runtime.
  const { data: buddies = [] } = useQuery({
    queryKey: ['buddies'],
    queryFn: () => getBuddies(),
    staleTime: Infinity,
  });

  const characters = buddies.map((b) => ({
    id: b.id,
    slug: b.slug,
    name: b.name,
    trait: TRAITS[b.slug] || '',
    img: `/assets/onboarding/${b.slug}.webp`,
  }));
  const total = characters.length;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const touchStartX = useRef(0);

  const currentStep = 2;
  const selectedChar = characters[selectedIndex] || null;

  // Desktop three-card view still uses prev/next + left/center/right indices
  const leftIdx   = total > 0 ? mod(selectedIndex - 1, total) : 0;
  const centerIdx = selectedIndex;
  const rightIdx  = total > 0 ? mod(selectedIndex + 1, total) : 0;

  function prev() { if (total > 0) setSelectedIndex((i) => mod(i - 1, total)); }
  function next() { if (total > 0) setSelectedIndex((i) => mod(i + 1, total)); }

  async function handleContinue() {
    setIsLoading(true);
    setError(null);
    try {
      // Unified draft-first flow (Packet B5): add-child and first-time
      // onboarding both arrive here with a draft learner already persisted.
      if (!activeLearner?.id) {
        navigate('/onboarding/child-profile', { replace: true });
        return;
      }
      if (!selectedChar) {
        setError(t('common.somethingWentWrong'));
        return;
      }
      const learner = await updateLearner(activeLearner.id, {
        buddy_id: selectedChar.id,
        onboarding_step: 2,
      });
      // FP-LEARNER-STORE-RETIREMENT-01: optimistic patch + invalidate so the
      // next page reads the new avatar/onboarding_step from React Query.
      queryClient.setQueryData(['learners'], (prev) =>
        Array.isArray(prev)
          ? prev.map((l) => (l.id === learner.id ? { ...l, ...learner } : l))
          : prev
      );
      setActiveLearnerId(learner.id);
      queryClient.invalidateQueries({ queryKey: ['learners'] });
      navigate('/onboarding/use-case');
    } catch (err) {
      setError(err.message || t('common.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main
      className={s.page}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* Desktop decoration — cream top band (337px) + cloud seam */}
      {/* These are hidden on mobile by their own CSS; mobile uses creamSection/tealSection */}
      <div className={`${shared.topBand} ${shared.topBandTeal}`} />
      <CloudBand variant="teal" className={shared.cloudBandTeal} />

      {/* Desktop logo (stays outside the mobile wrapper so desktop layout can show it) */}
      <WanderWordsLogo className={shared.logo} />

      {/* ───────────── MOBILE / TABLET LAYOUT (hidden ≥1200px) ───────────── */}
      <div className={`${shared.mobileOnly} ${s.mobileWrapper}`}>
        {/* Upper cream section */}
        <div className={s.creamSection}>
          {/* Step counter */}
          <div className={shared.stepCounter}>
            {Array.from({ length: STEP_COUNT }, (_, i) => {
              const step = i + 1;
              const isActive = step <= currentStep;
              return (
                <div
                  key={step}
                  className={`${shared.stepDot} ${isActive ? shared.stepDotActive : ''}`}
                  aria-label={`Step ${step} of ${STEP_COUNT}`}
                >
                  {step}
                </div>
              );
            })}
          </div>

          {/* Swipe hint */}
          <p className={s.swipeHint}>{t('buddy.swipeHint')}</p>

          {/* Mobile 3-card carousel — same as desktop, swipe to navigate */}
          <div
            className={s.mobileCarouselArea}
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const dx = touchStartX.current - e.changedTouches[0].clientX;
              if (Math.abs(dx) > 40) { dx > 0 ? next() : prev(); }
            }}
          >
            <div className={s.carouselTrack} role="radiogroup" aria-label="Choose your buddy">
              {[leftIdx, centerIdx, rightIdx].map((charIdx, position) => {
                const char = characters[charIdx];
                if (!char) return null;
                const isCenter = position === 1;
                return (
                  <button
                    key={char.slug}
                    type="button"
                    role="radio"
                    aria-checked={isCenter}
                    aria-label={char.name}
                    onClick={() => { if (!isCenter) { position === 0 ? prev() : next(); } }}
                    className={`${s.charCard} ${isCenter ? s.charCardCenter : s.charCardSide}`}
                  >
                    <img src={char.img} alt={char.name} className={s.charImg} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected character name + trait (only center character) */}
          {selectedChar && (
            <div className={s.charInfo}>
              <p className={s.charName}>{selectedChar.name}</p>
              <p className={s.charTrait}>{selectedChar.trait}</p>
            </div>
          )}
        </div>

        {/* Cloud-band seam */}
        <CloudBand variant="teal" className={s.cloudBand} />

        {/* Lower teal section */}
        <div className={s.tealSection}>
          {/* Title */}
          <h1 className={s.title}>{t('buddy.title')}</h1>
          <p className={s.subtitle}>{t('buddy.subtitle')}</p>

          {error && (
            <div className={shared.errorBanner} role="alert">{error}</div>
          )}

          {/* ToolTipLing — desktop only (component handles its own visibility) */}
          <ToolTipLing type="happy">
            {t('buddy.tooltipStep2')}
          </ToolTipLing>
        </div>

        {/* Continue button — fixed-bottom on mobile (BP-ONBOARDING-STICKY-BTN-01) */}
        <div className={s.stickyBtnArea}>
          <div className={shared.btnRow}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={shared.cancelBtn}
            >
              {t('common.back')}
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={isLoading}
              className={shared.continueBtn}
            >
              {isLoading ? t('buddy.gettingReady') : t('common.continue')}
            </button>
          </div>
        </div>
      </div>

      {/* ───────────── DESKTOP CARD (≥1200px only) ───────────── */}
      <OnboardingPageCard
        stepper={
          <div className={shared.stepCounter}>
            {Array.from({ length: STEP_COUNT }, (_, i) => {
              const step = i + 1;
              const isActive = step <= currentStep;
              return (
                <div
                  key={step}
                  className={`${shared.stepDot} ${isActive ? shared.stepDotActive : ''}`}
                  aria-label={`Step ${step} of ${STEP_COUNT}`}
                >
                  {step}
                </div>
              );
            })}
          </div>
        }
        leftWidth={345}
        leftContent={
          <>
            <div className={shared.headerColumn}>
              {/* Title */}
              <h1 className={shared.pageTitle}>{t('buddy.title')}</h1>
              <p className={shared.subtitle}>{t('buddy.subtitle')}</p>
            </div>
            {/* ToolTipLing at bottom */}
            <ToolTipLing type="happy">
              {t('buddy.tooltipStep2')}
            </ToolTipLing>
          </>
        }
        rightContent={
          <div className={s.desktopCarouselWrapper}>
            {/* Nav + carousel + name/trait */}
            <div className={s.desktopCarouselRow}>
              <button type="button" onClick={prev} className={s.navBtn} aria-label="Previous">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className={s.carouselTrack}>
                {[leftIdx, centerIdx, rightIdx].map((charIdx, position) => {
                  const char = characters[charIdx];
                  if (!char) return null;
                  const isCenter = position === 1;
                  return (
                    <button
                      key={char.slug}
                      type="button"
                      role="radio"
                      aria-checked={isCenter}
                      onClick={() => setSelectedIndex(charIdx)}
                      className={`${s.charCard} ${isCenter ? s.charCardCenter : s.charCardSide}`}
                    >
                      <img src={char.img} alt={char.name} className={s.charImg} />
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={next} className={s.navBtn} aria-label="Next">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            {/* Name + trait */}
            {selectedChar && (
              <div className={s.charInfo}>
                <p className={s.charName}>{selectedChar.name}</p>
                <p className={s.charTrait}>{selectedChar.trait}</p>
              </div>
            )}
            {/* Back + Continue */}
            <div className={shared.btnRow}>
              <button type="button" onClick={() => navigate(-1)} className={shared.cancelBtn}>
                {t('common.back')}
              </button>
              <button type="button" onClick={handleContinue} disabled={isLoading} className={shared.continueBtn}>
                {isLoading ? t('buddy.gettingReady') : t('common.continue')}
              </button>
            </div>
          </div>
        }
      />
    </main>
  );
}
